import { createStore } from 'vuex';
import axios from 'axios';

const ACCESS_TOKEN_KEY = 'accessToken';
const USER_DETAILS_KEY = 'userDetails';
const LOCATIONS_KEY = 'locations';
const CURRENT_LOCATION_ID_KEY = 'currentLocationId';
const CURRENT_LOCATION_NAME_KEY = 'currentLocationName';
const SELECTED_LOCATION_KEY = 'selectedLocation';

const store = createStore({
  state() {
    return {
      accessToken: localStorage.getItem(ACCESS_TOKEN_KEY) || null,
      userDetails: JSON.parse(localStorage.getItem(USER_DETAILS_KEY)) || null,
      isAuthenticated: !!localStorage.getItem(ACCESS_TOKEN_KEY),
      
      locations: JSON.parse(localStorage.getItem(LOCATIONS_KEY)) || [],
      currentLocationId: localStorage.getItem(CURRENT_LOCATION_ID_KEY) || null,
      currentLocationName: localStorage.getItem(CURRENT_LOCATION_NAME_KEY) || null,
      selectedLocation: localStorage.getItem(SELECTED_LOCATION_KEY) || null,
    };
  },
  mutations: {
    SET_AUTH_DATA(state, { accessToken, userDetails: rawUserAttributes }) {
      state.accessToken = accessToken;
      state.userDetails = rawUserAttributes;
      state.isAuthenticated = true;

      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(rawUserAttributes));

      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      const permissionsAttr = rawUserAttributes.find(a => a.Name === 'custom:permissions');
      let locations = [];

      if (permissionsAttr && permissionsAttr.Value) {
        let permValue = permissionsAttr.Value;
        if (typeof permValue === 'string') {
          try {
            permValue = JSON.parse(permValue);
          } catch (e) {
            console.error('Failed to parse custom:permissions JSON:', e);
            permValue = [];
          }
        }
        if (Array.isArray(permValue)) {
          locations = permValue.map(item => {
            const entry = Object.entries(item)[0];
            if (entry) {
              const [name, id] = entry;
              return { name, id };
            }
            return null;
          }).filter(Boolean);
        }
      }
      state.locations = [];
      state.currentLocationId = null;
      state.currentLocationName = null;
      localStorage.removeItem(LOCATIONS_KEY);
      localStorage.removeItem(CURRENT_LOCATION_ID_KEY);
      localStorage.removeItem(CURRENT_LOCATION_NAME_KEY);

      if (locations.length > 0) {
        state.locations = locations;
        localStorage.setItem(LOCATIONS_KEY, JSON.stringify(locations));

        if (locations.length === 1) {
          const loc = locations[0];
          state.currentLocationId = loc.id;
          state.currentLocationName = loc.name;
          localStorage.setItem(CURRENT_LOCATION_ID_KEY, loc.id);
          localStorage.setItem(CURRENT_LOCATION_NAME_KEY, loc.name);
        }
      }
    },
    CLEAR_AUTH_DATA(state) {
      state.accessToken = null;
      state.userDetails = null;
      state.isAuthenticated = false;
      state.locations = [];
      state.currentLocationId = null;
      state.currentLocationName = null;

      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(USER_DETAILS_KEY);
      localStorage.removeItem(LOCATIONS_KEY);
      localStorage.removeItem(CURRENT_LOCATION_ID_KEY);
      localStorage.removeItem(CURRENT_LOCATION_NAME_KEY);

      delete axios.defaults.headers.common['Authorization'];
    },
    SET_CURRENT_LOCATION(state, { id, name }) {
      state.currentLocationId = id;
      state.currentLocationName = name;
      localStorage.setItem(CURRENT_LOCATION_ID_KEY, id);
      localStorage.setItem(CURRENT_LOCATION_NAME_KEY, name);
    },
    setSelectedLocation(state, location) {
      state.selectedLocation = location;
      localStorage.setItem(SELECTED_LOCATION_KEY, location);
    },
  },
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await axios.post('/v1/auth/login', {
          email: credentials.email,
          password: credentials.password
        });

        const { access_token, UserAttributes } = response.data;
        commit('SET_AUTH_DATA', { accessToken: access_token, userDetails: UserAttributes });

        return response.data;
      } catch (error) {
        console.error('Login failed:', error.response || error);
        throw error;
      }
    },
    logout({ commit }) {
      commit('CLEAR_AUTH_DATA');
    },
    initializeAuth({ state }) {
      if (state.accessToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${state.accessToken}`;
      }
    },
    async setCurrentLocation({ commit }, { id, name }) {
    commit('SET_CURRENT_LOCATION', { id, name });

    // RELOAD PAGE AFTER LOCATION CHANGE
    window.location.reload();
  },
  },
  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    getUserDetails: (state) => state.userDetails,
    getUserRole: (state) => {
      const attributes = state.userDetails;
      if (!attributes || !Array.isArray(attributes)) return null;
      const roleAttr = attributes.find(a => a.Name === 'custom:role');
      return roleAttr ? roleAttr.Value : null;
    },
    isAdmin: (state) => {
      const attributes = state.userDetails;
      if (!attributes || !Array.isArray(attributes)) return false;
      const roleAttr = attributes.find(a => a.Name === 'custom:role');
      return roleAttr?.Value === 'admin';
    },
    getLocations: (state) => state.locations,
    getCurrentLocationId: (state) => state.currentLocationId,
    getCurrentLocationName: (state) => state.currentLocationName,
    hasMultipleLocations: (state) => state.locations.length > 1,
    getCurrentLocation: (state) => ({
      id: state.currentLocationId,
      name: state.currentLocationName,
    }),
    getSelectedLocation: (state) => state.selectedLocation,
  },
});

export default store;